import React, { useCallback, useEffect, useState } from "react";
import {
  BadgePercent,
  CreditCard,
  IndianRupee,
  Loader2,
  PhoneCall,
  Settings,
} from "lucide-react";
import { adminService } from "../../services/adminService";

const DEFAULT_SUBSCRIPTION_AMOUNT = 399900;
const DEFAULT_CURRENCY = "INR";

const initialDiscountForm = {
  code: "",
  discountType: "percent",
  discountValue: "",
  maxDiscountAmount: "",
  minOrderAmount: "",
  maxRedemptions: "",
  perEmailLimit: "1",
  active: true,
  validFrom: "",
  validUntil: "",
};

const PaymentSettings = () => {
  const [settings, setSettings] = useState(null);
  const [subscriptionAmountInput, setSubscriptionAmountInput] = useState("");
  const [callFirstEnabled, setCallFirstEnabled] = useState(false);
  const [callFirstAmountInput, setCallFirstAmountInput] = useState("");
  const [accountabilityAmountInput, setAccountabilityAmountInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(true);
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountUpdatingId, setDiscountUpdatingId] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [discountSuccess, setDiscountSuccess] = useState("");
  const [discountForm, setDiscountForm] = useState(initialDiscountForm);

  const applySettings = useCallback((payload) => {
    const subscriptionAmount =
      payload?.subscription_amount || DEFAULT_SUBSCRIPTION_AMOUNT;
    const callFirstAmount = payload?.call_first_amount || "";
    const accountabilityAmount = payload?.accountability_booking_amount || "";

    setSettings(payload || {});
    setSubscriptionAmountInput(String(subscriptionAmount / 100));
    setCallFirstEnabled(Boolean(payload?.call_first_subscribe_later_enabled));
    setCallFirstAmountInput(callFirstAmount ? String(callFirstAmount / 100) : "");
    setAccountabilityAmountInput(
      accountabilityAmount ? String(accountabilityAmount / 100) : ""
    );
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await adminService.getPaymentSettings();
      applySettings(payload);
      setSettingsLoaded(true);
    } catch (err) {
      console.error(err);
      setError("Failed to load payment settings.");
      setSettingsLoaded(false);
      applySettings({
        subscription_amount: DEFAULT_SUBSCRIPTION_AMOUNT,
        currency: DEFAULT_CURRENCY,
        call_first_subscribe_later_enabled: false,
        call_first_amount: null,
        accountability_booking_amount: null,
      });
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  const loadDiscountCodes = useCallback(async () => {
    setDiscountLoading(true);
    setDiscountError("");
    try {
      const payload = await adminService.getPaymentDiscountCodes();
      setDiscountCodes(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error(err);
      setDiscountError("Failed to load discount codes.");
      setDiscountCodes([]);
    } finally {
      setDiscountLoading(false);
    }
  }, []);

  const refreshPage = useCallback(() => {
    loadSettings();
    loadDiscountCodes();
  }, [loadDiscountCodes, loadSettings]);

  useEffect(() => {
    refreshPage();
  }, [refreshPage]);

  const handleSave = async () => {
    if (!settingsLoaded) {
      setError("Reload payment settings before saving.");
      setSuccess("");
      return;
    }

    const subscriptionAmountMajor = Number(subscriptionAmountInput);
    const callFirstAmountMajor = callFirstAmountInput
      ? Number(callFirstAmountInput)
      : null;
    const accountabilityAmountMajor = Number(accountabilityAmountInput);

    if (
      !Number.isFinite(subscriptionAmountMajor) ||
      subscriptionAmountMajor <= 0
    ) {
      setError("Enter a valid subscription amount greater than zero.");
      setSuccess("");
      return;
    }

    if (
      callFirstAmountMajor !== null &&
      (!Number.isFinite(callFirstAmountMajor) || callFirstAmountMajor <= 0)
    ) {
      setError("Enter a valid call-first amount greater than zero.");
      setSuccess("");
      return;
    }

    if (callFirstEnabled && callFirstAmountMajor === null) {
      setError("Enter a consultation price before enabling call bookings.");
      setSuccess("");
      return;
    }

    if (!Number.isFinite(accountabilityAmountMajor) || accountabilityAmountMajor <= 0) {
      setError("Enter a valid accountability booking price greater than zero.");
      setSuccess("");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = await adminService.updatePaymentSettings({
        subscription_amount: Math.round(subscriptionAmountMajor * 100),
        currency: settings?.currency || DEFAULT_CURRENCY,
        call_first_subscribe_later_enabled: callFirstEnabled,
        call_first_amount:
          callFirstAmountMajor === null
            ? null
            : Math.round(callFirstAmountMajor * 100),
        accountability_booking_amount: Math.round(accountabilityAmountMajor * 100),
      });
      applySettings(payload);
      setSuccess("Payment settings saved.");
    } catch (err) {
      console.error(err);
      setError("Failed to save payment settings.");
    } finally {
      setSaving(false);
    }
  };

  const updateDiscountForm = (field, value) => {
    setDiscountForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreateDiscountCode = async (event) => {
    event.preventDefault();

    const discountValue = Number(discountForm.discountValue);
    const maxDiscountAmount = parseOptionalPositiveNumber(discountForm.maxDiscountAmount);
    const minOrderAmount = parseOptionalPositiveNumber(discountForm.minOrderAmount);
    const maxRedemptions = parseOptionalPositiveInteger(discountForm.maxRedemptions);
    const perEmailLimit = parseOptionalPositiveInteger(discountForm.perEmailLimit);

    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      setDiscountError("Enter a valid discount value greater than zero.");
      setDiscountSuccess("");
      return;
    }

    if (discountForm.discountType === "percent" && discountValue > 100) {
      setDiscountError("Percentage discounts cannot be more than 100.");
      setDiscountSuccess("");
      return;
    }

    if (
      maxDiscountAmount === false ||
      minOrderAmount === false ||
      maxRedemptions === false ||
      perEmailLimit === false
    ) {
      setDiscountError("Optional limits must be positive whole numbers.");
      setDiscountSuccess("");
      return;
    }

    if (
      discountForm.validFrom &&
      discountForm.validUntil &&
      new Date(discountForm.validFrom) >= new Date(discountForm.validUntil)
    ) {
      setDiscountError("Valid until must be after valid from.");
      setDiscountSuccess("");
      return;
    }

    const payload = {
      code: discountForm.code.trim() || undefined,
      discount_type: discountForm.discountType,
      discount_value:
        discountForm.discountType === "fixed"
          ? Math.round(discountValue * 100)
          : Math.round(discountValue),
      max_discount_amount:
        maxDiscountAmount === null ? null : Math.round(maxDiscountAmount * 100),
      min_order_amount:
        minOrderAmount === null ? null : Math.round(minOrderAmount * 100),
      max_redemptions: maxRedemptions,
      per_email_limit: perEmailLimit || 1,
      active: discountForm.active,
      valid_from: discountForm.validFrom
        ? new Date(discountForm.validFrom).toISOString()
        : null,
      valid_until: discountForm.validUntil
        ? new Date(discountForm.validUntil).toISOString()
        : null,
    };

    setDiscountSaving(true);
    setDiscountError("");
    setDiscountSuccess("");
    try {
      const created = await adminService.createPaymentDiscountCode(payload);
      setDiscountForm(initialDiscountForm);
      setDiscountSuccess(`Discount code ${created.code} created.`);
      await loadDiscountCodes();
    } catch (err) {
      console.error(err);
      setDiscountError(
        err?.response?.data?.detail || "Failed to create discount code."
      );
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleToggleDiscountCode = async (code) => {
    setDiscountUpdatingId(code.id);
    setDiscountError("");
    setDiscountSuccess("");
    try {
      const updated = await adminService.updatePaymentDiscountCode(code.id, {
        active: !code.active,
      });
      setDiscountCodes((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      setDiscountSuccess(
        `Discount code ${updated.code} ${updated.active ? "activated" : "deactivated"}.`
      );
    } catch (err) {
      console.error(err);
      setDiscountError(
        err?.response?.data?.detail || "Failed to update discount code."
      );
    } finally {
      setDiscountUpdatingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-display">Payment Settings</h1>
          <p className="text-sm text-muted">
            Configure learner subscription pricing, discount codes, and future payment flows.
          </p>
        </div>
        <button
          type="button"
          onClick={refreshPage}
          disabled={loading || discountLoading}
          className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-title shadow-sm hover:bg-gray-50 disabled:opacity-60"
        >
          {loading || discountLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Settings size={16} />
          )}
          Refresh
        </button>
      </div>

      <section className="rounded-lg bg-white p-4 shadow stakeholder-summary-card">
        <div>
          <h2 className="text-sm font-semibold text-title">Pricing & booking</h2>
          <p className="mt-1 text-sm text-muted">Manage the prices learners see across paid flows.</p>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          <PriceCard icon={CreditCard} title="Subscription" description="Learner subscription price">
            <PriceInput value={subscriptionAmountInput} onChange={setSubscriptionAmountInput} disabled={!settingsLoaded || saving} label="Subscription price" />
          </PriceCard>

          <PriceCard icon={Settings} title="Accountability call" description="One-to-one accountability booking">
            <PriceInput value={accountabilityAmountInput} onChange={setAccountabilityAmountInput} disabled={!settingsLoaded || saving} label="Booking price" />
          </PriceCard>

          <PriceCard icon={PhoneCall} title="Consultation call" description="Call first, subscribe later">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-medium text-muted">Show on website</span>
              <label className="inline-flex cursor-pointer">
                <input type="checkbox" role="switch" aria-label="Show consultation bookings on website" checked={callFirstEnabled} disabled={!settingsLoaded || saving} onChange={(event) => setCallFirstEnabled(event.target.checked)} className="peer sr-only" />
                <span className="relative h-6 w-11 rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:content-[''] peer-checked:bg-[#54B0AF] peer-checked:after:translate-x-5 peer-disabled:opacity-50" />
              </label>
            </div>
            <PriceInput value={callFirstAmountInput} onChange={setCallFirstAmountInput} disabled={!settingsLoaded || saving || !callFirstEnabled} label="Consultation price" />
          </PriceCard>
        </div>

        <div className="mt-4 flex flex-col-reverse gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          {(error || success) ? (
            <span className={`text-sm ${error ? "text-red-600" : "text-emerald-700"}`}>{error || success}</span>
          ) : <span />}
          <button type="button" onClick={handleSave} disabled={saving || !settingsLoaded} className="rounded-md bg-[#54B0AF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </section>

      <section className="rounded-lg bg-white p-4 shadow stakeholder-summary-card">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-title">
              <BadgePercent size={17} className="text-[#54B0AF]" />
              Discount Codes
            </div>
            <p className="mt-1 text-sm text-muted">
              Create checkout discounts for subscription payments.
            </p>
          </div>
          <div className="text-xs text-muted">
            {discountCodes.length} configured
          </div>
        </div>

        <form
          onSubmit={handleCreateDiscountCode}
          className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4"
        >
          <label className="text-sm text-title">
            Code
            <input
              type="text"
              value={discountForm.code}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("code", event.target.value.toUpperCase())}
              placeholder="Auto-generate"
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm uppercase disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <label className="text-sm text-title">
            Discount type
            <select
              value={discountForm.discountType}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("discountType", event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </label>

          <label className="text-sm text-title">
            {discountForm.discountType === "fixed" ? "Discount in INR" : "Discount percent"}
            <input
              type="number"
              min="1"
              max={discountForm.discountType === "percent" ? "100" : undefined}
              step="1"
              value={discountForm.discountValue}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("discountValue", event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <label className="text-sm text-title">
            Max discount in INR
            <input
              type="number"
              min="1"
              step="1"
              value={discountForm.maxDiscountAmount}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("maxDiscountAmount", event.target.value)}
              placeholder="Optional"
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <label className="text-sm text-title">
            Min order in INR
            <input
              type="number"
              min="1"
              step="1"
              value={discountForm.minOrderAmount}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("minOrderAmount", event.target.value)}
              placeholder="Optional"
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <label className="text-sm text-title">
            Max redemptions
            <input
              type="number"
              min="1"
              step="1"
              value={discountForm.maxRedemptions}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("maxRedemptions", event.target.value)}
              placeholder="Optional"
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <label className="text-sm text-title">
            Per-email limit
            <input
              type="number"
              min="1"
              step="1"
              value={discountForm.perEmailLimit}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("perEmailLimit", event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <label className="inline-flex items-center gap-2 self-end pb-2 text-sm font-medium text-title">
            <input
              type="checkbox"
              checked={discountForm.active}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("active", event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#54B0AF]"
            />
            Active
          </label>

          <label className="text-sm text-title">
            Valid from
            <input
              type="datetime-local"
              value={discountForm.validFrom}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("validFrom", event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <label className="text-sm text-title">
            Valid until
            <input
              type="datetime-local"
              value={discountForm.validUntil}
              disabled={discountSaving}
              onChange={(event) => updateDiscountForm("validUntil", event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={discountSaving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#54B0AF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 md:w-auto"
            >
              {discountSaving && <Loader2 size={16} className="animate-spin" />}
              {discountSaving ? "Creating..." : "Create Code"}
            </button>
          </div>
        </form>

        {(discountError || discountSuccess) && (
          <div
            className={`mt-4 rounded-md px-4 py-3 text-sm ${
              discountError
                ? "border border-red-100 bg-red-50 text-red-700"
                : "border border-emerald-100 bg-emerald-50 text-emerald-700"
            }`}
          >
            {discountError || discountSuccess}
          </div>
        )}

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-4 font-semibold">Code</th>
                <th className="py-2 pr-4 font-semibold">Discount</th>
                <th className="py-2 pr-4 font-semibold">Limits</th>
                <th className="py-2 pr-4 font-semibold">Validity</th>
                <th className="py-2 pr-4 font-semibold">Usage</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {discountLoading ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-muted">
                    Loading discount codes...
                  </td>
                </tr>
              ) : discountCodes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-muted">
                    No discount codes configured yet.
                  </td>
                </tr>
              ) : (
                discountCodes.map((code) => (
                  <tr key={code.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-3 pr-4 font-semibold text-title">{code.code}</td>
                    <td className="py-3 pr-4 text-title">{formatDiscountValue(code)}</td>
                    <td className="py-3 pr-4 text-muted">{formatDiscountLimits(code)}</td>
                    <td className="py-3 pr-4 text-muted">{formatValidity(code)}</td>
                    <td className="py-3 pr-4 text-muted">
                      {code.redeemed_count || 0}
                      {code.max_redemptions ? ` / ${code.max_redemptions}` : ""}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          code.active
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {code.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        disabled={discountUpdatingId === code.id}
                        onClick={() => handleToggleDiscountCode(code)}
                        className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-title hover:bg-gray-50 disabled:opacity-60"
                      >
                        {discountUpdatingId === code.id
                          ? "Updating..."
                          : code.active
                            ? "Deactivate"
                            : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default PaymentSettings;

const PriceCard = ({ icon: Icon, title, description, children }) => (
  <div className="flex min-h-[190px] flex-col rounded-lg border border-gray-200 bg-gray-50/50 p-4">
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-[#3b9d9b] shadow-sm">
        <Icon size={17} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-title">{title}</h3>
        <p className="mt-0.5 text-xs text-muted">{description}</p>
      </div>
    </div>
    <div className="mt-auto pt-5">{children}</div>
  </div>
);

const PriceInput = ({ value, onChange, disabled, label }) => {
  const inputId = React.useId();
  return (
  <div className="block text-xs font-medium text-title">
    <label htmlFor={inputId}>{label}</label>
    <div className="relative mt-1.5">
      <IndianRupee size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
      <input
        id={inputId}
        type="number"
        min="1"
        step="1"
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="block w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-14 text-sm font-medium text-title shadow-sm outline-none transition focus:border-[#54B0AF] focus:ring-2 focus:ring-[#54B0AF]/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">INR</span>
    </div>
  </div>
  );
};

const formatCurrency = (amount, currency = DEFAULT_CURRENCY) => {
  if (!amount) return "--";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount / 100);
};

const formatDiscountValue = (code) => {
  if (code.discount_type === "percent") {
    return `${code.discount_value}%`;
  }
  return formatCurrency(code.discount_value);
};

const formatDiscountLimits = (code) => {
  const limits = [];
  if (code.max_discount_amount) {
    limits.push(`Max ${formatCurrency(code.max_discount_amount)}`);
  }
  if (code.min_order_amount) {
    limits.push(`Min order ${formatCurrency(code.min_order_amount)}`);
  }
  if (code.per_email_limit) {
    limits.push(`${code.per_email_limit} per email`);
  }
  return limits.length ? limits.join(", ") : "--";
};

const formatValidity = (code) => {
  const from = code.valid_from ? formatDateTime(code.valid_from) : null;
  const until = code.valid_until ? formatDateTime(code.valid_until) : null;
  if (from && until) return `${from} to ${until}`;
  if (from) return `From ${from}`;
  if (until) return `Until ${until}`;
  return "No expiry";
};

const formatDateTime = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

const parseOptionalPositiveNumber = (value) => {
  if (value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return false;
  return parsed;
};

const parseOptionalPositiveInteger = (value) => {
  if (value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return false;
  return parsed;
};
